<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFormsStore, useSectionsStore } from '@/stores';
import type { SectionDto, FormDto, Locales } from '@/services/types/api.types';
import { getTranslation } from '@/composables/data-display-utils';

// PROPS
const props = defineProps<{
  consultationId: number;
  languageEnabled: Locales[];
  section?: SectionDto | null;
  form?: FormDto | null;
}>();

// EMITS
const emit = defineEmits<{
  'section-selected': [section: SectionDto];
  'section-cleared': [];
  'form-selected': [form: FormDto];
  'form-cleared': [];
}>();

// COMPOSABLES
const { t } = useI18n();
const formsStore = useFormsStore();
const sectionsStore = useSectionsStore();

// REACTIVE STATE
const selectedSection = ref<SectionDto | null>(null);
const selectedForm = ref<FormDto | null>(null);
const formsList = ref<FormDto[]>([]);

// COMPUTED
const sections = sectionsStore.getSectionsByConsultationId(props.consultationId);
const sectionsList = computed(() => sections ?? []);

// FUNCTIONS
const getForms = async (id: number) => {
  await formsStore.fetchFormsBySection(id);
  formsList.value = formsStore.getFormsBySectionId(id) ?? [];
};

// WATCHERS
watch(selectedSection, (newValue: SectionDto | null) => {
  if (newValue && typeof newValue.id !== 'undefined') {
    getForms(newValue.id!);
    selectedForm.value = null;
    emit('section-selected', newValue);
  } else {
    selectedForm.value = null;
    emit('section-cleared');
  }
});

watch(selectedForm, (newValue: FormDto | null) => {
  if (newValue !== null) {
    emit('form-selected', newValue);
  } else {
    emit('form-cleared');
  }
});

// Sync from parent-driven selection
watch(
  () => props.section,
  (val) => {
    selectedSection.value = val ?? null;
    if (val?.id) {
      getForms(val.id);
    } else {
      formsList.value = [];
    }
  },
  { immediate: true },
);

watch(
  () => props.form,
  (val) => {
    selectedForm.value = val ?? null;
  },
  { immediate: true },
);
</script>

<template>
  <div class="section-form-selector">
    <q-select
      v-model="selectedSection"
      :options="sectionsList"
      :option-label="(section) => getTranslation(section, 'title', props.languageEnabled)"
      :label="t('consultation.tabs.sections')"
      clearable
      outlined
    />
    <q-select
      v-if="selectedSection != null"
      v-model="selectedForm"
      :options="formsList"
      :option-label="(form) => getTranslation(form.question, 'title', props.languageEnabled)"
      :label="t('form.list')"
      clearable
      outlined
    >
      <template v-if="$slots['form-option-badges']" #option="scope">
        <q-item v-bind="scope.itemProps" class="form-option-item">
          <q-item-section>
            <q-item-label>{{ getTranslation(scope.opt.question, 'title', props.languageEnabled) }}</q-item-label>
          </q-item-section>
          <q-item-section side class="badges-section">
            <slot name="form-option-badges" :form="scope.opt" />
          </q-item-section>
        </q-item>
      </template>
    </q-select>
  </div>
</template>

<style lang="scss" scoped>
.section-form-selector {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.form-option-item {
  .badges-section {
    margin-left: 8px;
  }
}
</style>
